import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@learn-me/ui";
import { categoriesApi } from "@/features/categories/api/categoriesApi";
import toast from "react-hot-toast";
import { handleApiError } from "@/lib/errorHandler";
import type { Category } from "@learn-me/types";
import { X } from "lucide-react";

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: Category | null;
  mainAreasCount: number;
}

interface CategoryFormData {
  name: string;
  description: string;
  iconUrl: string;
  isMainArea: boolean;
  position: number;
}

export function CategoryModal({
  isOpen,
  onClose,
  category,
  mainAreasCount,
}: CategoryModalProps) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement>(null);
  const isEditing = !!category;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CategoryFormData>({
    defaultValues: {
      name: "",
      description: "",
      iconUrl: "",
      isMainArea: false,
      position: 0,
    },
  });

  const isMainArea = watch("isMainArea");

  useEffect(() => {
    if (category) {
      reset({
        name: category.name,
        description: category.description || "",
        iconUrl: category.iconUrl || "",
        isMainArea: category.isMainArea || false,
        position: category.position || 0,
      });
    } else {
      reset({
        name: "",
        description: "",
        iconUrl: "",
        isMainArea: false,
        position: mainAreasCount + 1,
      });
    }
  }, [category, reset, mainAreasCount]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const createMutation = useMutation({
    mutationFn: categoriesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categoría creada exitosamente");
      handleClose();
    },
    onError: (error) => {
      handleApiError(error, "Error al crear la categoría");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CategoryFormData>;
    }) => categoriesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categoría actualizada exitosamente");
      handleClose();
    },
    onError: (error) => {
      handleApiError(error, "Error al actualizar la categoría");
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const onSubmit = (data: CategoryFormData) => {
    const payload = {
      name: data.name.trim(),
      description: data.description?.trim() || undefined,
      iconUrl: data.iconUrl?.trim() || undefined,
      isMainArea: data.isMainArea,
      position: data.isMainArea ? data.position : 0,
    };

    if (isEditing && category) {
      updateMutation.mutate({ id: category.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const canBeMainArea = isEditing
    ? category?.isMainArea || mainAreasCount < 3
    : mainAreasCount < 3;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        className="relative z-10 w-full max-w-md mx-4 bg-white rounded-lg shadow-xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar Categoría" : "Nueva Categoría"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium">
              Nombre *
            </label>
            <Input
              id="name"
              placeholder="Ej: Cursos Libres"
              {...register("name", {
                required: "El nombre es requerido",
                minLength: {
                  value: 2,
                  message: "El nombre debe tener al menos 2 caracteres",
                },
              })}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="description" className="block text-sm font-medium">
              Descripción
            </label>
            <textarea
              id="description"
              className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Descripción de la categoría..."
              {...register("description")}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="iconUrl" className="block text-sm font-medium">
              URL del Icono
            </label>
            <Input
              id="iconUrl"
              type="text"
              placeholder="https://..."
              {...register("iconUrl")}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <label htmlFor="isMainArea" className="text-sm font-medium">
                Área de Formación Principal
              </label>
              <p className="text-sm text-muted-foreground">
                {canBeMainArea
                  ? "Se mostrará en la página de inicio"
                  : "Ya hay 3 áreas principales (máximo permitido)"}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isMainArea}
              onClick={() =>
                canBeMainArea && setValue("isMainArea", !isMainArea)
              }
              disabled={!canBeMainArea}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isMainArea ? "bg-primary" : "bg-gray-200"
              } ${!canBeMainArea ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isMainArea ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {isMainArea && (
            <div className="space-y-2">
              <label htmlFor="position" className="block text-sm font-medium">
                Posición (1-3)
              </label>
              <Input
                id="position"
                type="number"
                min={1}
                max={3}
                {...register("position", {
                  valueAsNumber: true,
                  min: { value: 1, message: "Mínimo 1" },
                  max: { value: 3, message: "Máximo 3" },
                })}
              />
              {errors.position && (
                <p className="text-sm text-destructive">
                  {errors.position.message}
                </p>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Guardando..." : isEditing ? "Actualizar" : "Crear"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
